import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  BILLING_COMPANY_ID,
  INVOICE_STATUSES,
  type Invoice,
  type InvoiceStatus,
} from '@accounting/shared';
import { BillingImportPanel } from '../../components/invoices/BillingImportPanel';
import { useInvoices } from '../../hooks/useInvoices';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/invoices/')({
  component: InvoicesPage,
});

export function invoiceStatusTag(inv: Pick<Invoice, 'status' | 'isOverdue'>) {
  if (inv.status === 'draft') return <span className="tag">in progress</span>;
  if (inv.status === 'paid') return <span className="tag ok">paid</span>;
  if (inv.status === 'void') return <span className="tag bad">void</span>;
  if (inv.isOverdue) return <span className="tag bad">past due</span>;
  if (inv.status === 'partial') return <span className="tag warn">partial</span>;
  return <span className="tag warn">open</span>;
}

function InvoicesPage() {
  const companyId = useActiveCompanyId();
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const { data: invoices = [], isLoading, error } = useInvoices(companyId, {
    status: status || undefined,
  });

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Invoices</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Invoices</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Accounts receivable</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/customer-center" className="btn-secondary" style={{ textDecoration: 'none' }}>
            Customer Center
          </Link>
          <label style={{ fontSize: 13 }}>
            Status{' '}
            <select value={status} onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}>
              <option value="">All</option>
              {INVOICE_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <Link to="/invoices/new" className="btn-primary" style={{ textDecoration: 'none' }}>
            + New invoice
          </Link>
        </div>
      </div>

      {/* Advisory-fee CSV billing is specific to how BCM bills its managed
          accounts — the account codes it posts to mean other things at other
          companies — so the panel appears for that company alone. */}
      {companyId === BILLING_COMPANY_ID && <BillingImportPanel companyId={companyId} />}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load invoices.</p>}

      {!isLoading && invoices.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No invoices yet.</p>
          <Link to="/invoices/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Create your first invoice
          </Link>
        </div>
      )}

      {invoices.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Due</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} style={{ opacity: inv.status === 'void' ? 0.6 : 1 }}>
                <td>{inv.invoiceNumber}</td>
                <td>{inv.customerName ?? <span className="muted">—</span>}</td>
                <td>{formatAccountingDate(inv.invoiceDate)}</td>
                <td>{formatAccountingDate(inv.dueDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(inv.total)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(inv.outstandingAmount)}</td>
                <td>{invoiceStatusTag(inv)}</td>
                <td>
                  <Link to="/invoices/$id" params={{ id: inv.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
